/**
 * `/reports/deck/:mine` — the eight-slide equipment summary of one mine.
 * Port of the demo's `buildDeck(m)` (demo line 2613) in its report pane.
 *
 * The slides render as stacked panels in the page, the way the demo showed
 * them, and download as one HTML file with each slide on its own print page.
 * The figures that came from rows open them: availability and emissions open
 * the week's shift records, and each breakdown on slide 6 opens its work order.
 *
 * @see src/versions/v1/modules/reports/DeckScene.transform.ts
 * @see docs/logic/reports/deck.md
 */

import { useMemo, useRef, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  HelpAnchor,
  InlineError,
  SkeletonRows,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { HEALTH } from '../../data/core';
import { CONDITION_BAND_LABEL, type MineCode } from '../../schema/machine';
import type { DeckPack, ReportRef, ReportSource } from '../../schema/report';
import { unpackTable } from '../../shared/columnar';
import { DeckCover } from '../../shared/docs/DeckCover';
import { DocFoot } from '../../shared/docs/DocFoot';
import { useOemDocIdentity } from '../../shared/docs/useOemDocIdentity';
import { fmt$, fmtk, groupNum, inWk, WK } from '../../shared/oem-engine';
import { BandPill, SourceLink } from '../../shared/oem-tiles';
import { useOemPersona } from '../../shared/OemPersonaContext';
import { isMineCode } from '../../shared/report-catalog';
import { useOemData, type ShiftDailyRow, type WorkOrder } from '../../shared/useOemData';
import { BarChart } from '../../shared/viz/BarChart';
import { LineChart } from '../../shared/viz/LineChart';
import type { SourceCalc } from '../../chrome-oem/SourceDrawerBody';
import { useRowsDrawer } from '../service/source-slice';
import { NotFoundScene } from '../NotFoundScene';
import { ReportSidePanel, ReportToolbar } from './ReportActions';
import { availabilityCalc, emissionsCalc } from './report-calc';
import { MachineLink, Para, ReportLayout, ReportPaper } from './ReportDoc';
import { deckReport, monthTick } from './DeckScene.transform';

export function DeckScene() {
  const { mine } = useParams<{ mine: string }>();
  const code = mine?.toUpperCase();
  if (!isMineCode(code)) return <NotFoundScene />;
  return <DeckView mine={code} />;
}

function DeckView({ mine }: { mine: MineCode }) {
  const workOrdersData = useOemData('work-orders');
  const telemetry = useOemData('telemetry');
  const parts = useOemData('parts');
  const costs = useOemData('costs');
  const reports = useOemData('reports');
  const oem = useOemDocIdentity();
  const { persona } = useOemPersona();
  const openRows = useRowsDrawer();
  const paperRef = useRef<HTMLDivElement>(null);
  const address: ReportRef = { kind: 'deck', mine };

  const workOrders = useMemo(
    () => (workOrdersData.data ? unpackTable<WorkOrder>(workOrdersData.data.wos) : null),
    [workOrdersData.data],
  );
  const shiftDaily = useMemo(
    () => (telemetry.data ? unpackTable<ShiftDailyRow>(telemetry.data.shift_daily) : null),
    [telemetry.data],
  );

  const report = useMemo(() => {
    if (!workOrders || !shiftDaily || !telemetry.data || !parts.data || !costs.data || !reports.data) {
      return null;
    }
    const pack = reports.data.reports.deck_br as unknown as DeckPack | undefined;
    if (!pack) return null;
    return deckReport(
      mine,
      {
        workOrders,
        shiftDaily,
        inventory: parts.data.inventory,
        costByMonth: costs.data.cost_by_month_mine,
        energy: telemetry.data.energy,
        pack,
      },
      oem,
    );
  }, [costs.data, mine, oem, parts.data, reports.data, shiftDaily, telemetry.data, workOrders]);

  const error =
    workOrdersData.error ?? telemetry.error ?? parts.error ?? costs.error ?? reports.error;

  const machineIds = useMemo(() => report?.condition.map((r) => r.machine_id) ?? [], [report]);
  const weekRows = () =>
    (shiftDaily ?? []).filter((s) => machineIds.includes(s.machine_id) && inWk(s.date));
  const openShift = (calc?: SourceCalc) =>
    openRows('shift', weekRows(), {
      title: 'Shift records this week',
      filter: `mine=${mine}, week to 06 Sep`,
      total: shiftDaily?.length,
      ...(calc ? { calc } : {}),
    });
  const openWo = (woId: string) =>
    openRows('wos', (workOrders ?? []).filter((w) => w.wo_id === woId), {
      title: `Work order ${woId}`,
      filter: `wo_id=${woId}`,
    });

  const onSource = (s: ReportSource) => {
    if (s.dataset === 'shift') return () => openShift();
    if (s.dataset === 'health') {
      return () =>
        openRows('health', HEALTH.filter((h) => machineIds.includes(h.machine_id)), {
          title: 'Condition register',
          filter: `mine=${mine}`,
        });
    }
    return null;
  };

  return (
    <SurfacePageLayout width="full">
      <Commentable name="DeckScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-5" as="div">
            <SurfacePageHeader
              kicker="Reports"
              title={report?.title ?? 'Equipment summary'}
              description={
                report
                  ? `${report.sub} · generated 06:05 by the assistant for ${persona.name}`
                  : 'Building the deck from the live workspace data…'
              }
              actions={<HelpAnchor doc="reports/deck" />}
            />

            <ReportToolbar report={report} address={address} docRef={paperRef} />
            <AskStrip view="reports" />

            {error ? (
              <InlineError>{`The deck did not build: ${error.message}`}</InlineError>
            ) : !report ? (
              <SkeletonRows count={12} />
            ) : (
              <ReportLayout
                doc={
                  <Commentable name="DeckSlides">
                    <ReportPaper paperRef={paperRef} label={report.title}>
                      <DeckCover {...report.cover} />

                      <Slide n={2} title="At a glance">
                        <div className="kpis grid grid-cols-1 gap-2.5 @xl:grid-cols-2 @4xl:grid-cols-4">
                          <Kpi label="Availability" unit={`% ${WK.short}`}>
                            <SourceLink
                              onClick={() =>
                                openShift(availabilityCalc(weekRows(), report.glance.availability, 'deckAvailability'))
                              }
                              className="text-[18px]"
                            >
                              {report.glance.availability}
                            </SourceLink>
                          </Kpi>
                          <Kpi label="All-in cost · 18 months">{fmtk(report.glance.cost18)}</Kpi>
                          <Kpi label="Cost per operating hour" unit={report.glance.cphOthers}>
                            A${report.glance.cph}
                          </Kpi>
                          <Kpi
                            label="CO₂-e this week"
                            unit={`t · ${groupNum(report.glance.diesel)} L · ${groupNum(report.glance.kwh)} kWh`}
                          >
                            <SourceLink
                              onClick={() => openShift(emissionsCalc(weekRows(), report.glance.co2_t))}
                              className="text-[18px]"
                            >
                              {report.glance.co2_t}
                            </SourceLink>
                          </Kpi>
                        </div>
                      </Slide>

                      <Slide n={3} title="Cost per operating hour by machine class">
                        <BarChart
                          title="A$ per operating hour, all-in (18 months)"
                          rows={report.costPerClass.map((c) => ({ label: c.label, value: c.value }))}
                          format={(v) => `A$${Math.round(v)}`}
                          gutter={190}
                        />
                        <Para note>A$ per operating hour, all-in (18 months).</Para>
                      </Slide>

                      <Slide n={4} title="Monthly maintenance spend">
                        <LineChart
                          labels={report.monthly.map((x) => monthTick(x.month))}
                          series={[
                            {
                              values: report.monthly.map((x) => Math.round(x.amount / 1000)),
                              color: 'var(--accent)',
                              dots: true,
                            },
                          ]}
                          min={0}
                          width={700}
                          height={220}
                          xticks={18}
                          xTitle="Month (yy/mm)"
                          yTitle="A$ thousand"
                          ariaLabel="Monthly maintenance spend, A$ thousand"
                          className="w-full"
                        />
                        <Para note>
                          By category over 18 months:{' '}
                          {report.byCategory.map((c) => `${c.category} ${fmtk(c.amount)}`).join(' · ')}
                        </Para>
                      </Slide>

                      <Slide n={5} title={`Fleet condition — ${report.week.n} machines`}>
                        <Table className="min-w-[760px]">
                          <THead>
                            <TR>
                              <TH>Machine</TH>
                              <TH>Model</TH>
                              <TH className="num text-right">Hours</TH>
                              <TH className="num text-right">Avail %</TH>
                              <TH>Condition</TH>
                              <TH>Lowest component</TH>
                              <TH className="num text-right">Score</TH>
                              <TH className="num text-right">A$/op h</TH>
                            </TR>
                          </THead>
                          <TBody>
                            {report.condition.map((r) => (
                              <TR key={r.machine_id}>
                                <TD>
                                  <MachineLink id={r.machine_id} />
                                </TD>
                                <TD>{r.model}</TD>
                                <TD className="num mono text-right tabular-nums">{groupNum(r.engine_hours)}</TD>
                                <TD className="num mono text-right tabular-nums">{r.avail ?? '—'}</TD>
                                <TD>
                                  <BandPill band={r.band}>{CONDITION_BAND_LABEL[r.band]}</BandPill>
                                </TD>
                                <TD className="text-[11.5px]">{r.worst}</TD>
                                <TD className="num mono text-right tabular-nums">{r.score}</TD>
                                <TD className="num mono text-right tabular-nums">{r.cph ?? '—'}</TD>
                              </TR>
                            ))}
                          </TBody>
                        </Table>
                      </Slide>

                      <Slide n={6} title={report.alert ? 'Open predictive alert' : 'Breakdown history'}>
                        {report.alert ? (
                          <p className="text-[12.5px]" style={{ color: 'var(--ink-secondary)' }}>
                            <b style={{ color: 'var(--ink-primary)' }}>
                              {report.alert.machine} · {report.alert.comp}
                            </b>{' '}
                            — {report.alert.title}. Window: {report.alert.window}; model confidence{' '}
                            {Math.round(report.alert.confidence * 100)}%. {report.alert.why}{' '}
                            <Link
                              to={`/issues/alerts/${report.alert.id}`}
                              className="underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
                              style={{ color: 'var(--accent)' }}
                            >
                              open alert
                            </Link>
                          </p>
                        ) : null}
                        {report.breakdowns.length ? (
                          <Para note>
                            Largest breakdowns, 18 months:{' '}
                            {report.breakdowns.map((w, i) => (
                              <span key={`${w.wo_id}-${i}`}>
                                {i ? '; ' : ''}
                                <SourceLink onClick={() => openWo(w.wo_id)} className="mono">
                                  {w.wo_id}
                                </SourceLink>{' '}
                                {w.machine_id} {w.component} {w.date} · {fmt$(w.allin)} all-in
                              </span>
                            ))}
                          </Para>
                        ) : null}
                      </Slide>

                      <Slide n={7} title="Energy and emissions">
                        <BarChart
                          title="kg CO₂-e per operating hour (12-day average)"
                          rows={report.energy.map((e) => ({
                            label: e.drive,
                            value: e.co2PerHour,
                            detail: `${e.machines} machine${e.machines === 1 ? '' : 's'}`,
                          }))}
                          format={(v) => v.toFixed(1)}
                          gutter={190}
                        />
                        <Para note>
                          Week: {groupNum(report.week.diesel)} L diesel, {groupNum(report.week.kwh)} kWh,{' '}
                          {report.week.co2_t} t CO₂-e across {report.week.n} machines · kg CO₂-e per
                          operating hour, 12-day average.
                        </Para>
                      </Slide>

                      <Slide n={8} title="Recommendations">
                        <ol className="m-0 list-decimal space-y-1 pl-5 text-[12.5px]" style={{ color: 'var(--ink-secondary)' }}>
                          {report.recommendations.map((r) => (
                            <li key={r}>{r}</li>
                          ))}
                        </ol>
                      </Slide>

                      <DocFoot docId={report.docId} controlled={false} />
                    </ReportPaper>
                  </Commentable>
                }
                side={
                  <Commentable name="DeckSources">
                    <div className="min-w-0">
                      <ReportSidePanel report={report} address={address} onSource={onSource} />
                    </div>
                  </Commentable>
                }
              />
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** One slide — the demo's `.slide` panel with its numbered heading. */
function Slide({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section
      className="slide mb-2.5 min-w-0 rounded-lg border px-4 py-3.5"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
      aria-label={`Slide ${n}: ${title}`}
    >
      <h4 className="mb-1.5 text-[13px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
        {n} · {title}
      </h4>
      {children}
    </section>
  );
}

/** A figure on slide 2 — the demo's `.card.kpi`. */
function Kpi({ label, unit, children }: { label: string; unit?: string; children: ReactNode }) {
  return (
    <div
      className="kpi min-w-0 rounded-lg border px-3 py-2"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-1)' }}
    >
      <div
        className="text-[10px] font-semibold uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-tertiary)' }}
      >
        {label}
      </div>
      <div className="mono text-[18px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
        {children}
      </div>
      {unit ? (
        <div className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
          {unit}
        </div>
      ) : null}
    </div>
  );
}
